import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { vars } from '../../theme';
import { Close16, IconProps } from '@honeybook/icons';
import HBLink, { ILinkProps } from '../HBLink/HBLink';
import HBText from '../HBText/HBText';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';

export interface INoteProps {
    /** Icon component rendered at the start of the note; receives `color` as a prop. */
    icon: (props: IconProps) => JSX.Element;
    /** Optional heading text rendered above the description. */
    title?: ReactNode;
    /** Main body content of the note. Supports multiline text via `white-space: pre-line`. */
    description: ReactNode;
    /** CSS class applied to the root element. */
    className?: string;
    /** When provided, renders a dismiss (close) button in the top-right corner. */
    onDismiss?: () => void;
    /** Renders a link action at the bottom-right of the note. Supports both `onClick` handler and `href` navigation. */
    action?: {
        text: ILinkProps['children'];
        onClick?: ILinkProps['onClick'];
        href?: ILinkProps['href'];
        isNewTab?: boolean;
    };
}

const HBNote = ({
    icon: Icon,
    title,
    description,
    className,
    action,
    onDismiss,
    testId,
    testIdName
}: INoteProps & MustIncludeOneOfBasicTestIdProps) => {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'link',
        componentName: 'HBNote'
    });

    return (
        <Container className={className}>
            <Icon color={vars.colors.text.accent} />

            {title && <Title color="text.primary">{title}</Title>}

            <Description color="text.secondary">{description}</Description>

            {onDismiss && (
                <Close color={vars.colors.text.tertiary} onClick={onDismiss} />
            )}

            {action && (
                <Link
                    noUnderline
                    textType="emphasis14"
                    color={vars.colors.text.accent}
                    href={action.href}
                    onClick={action.onClick}
                    target={action.isNewTab ? '_blank' : undefined}
                    testId={finalTestId}
                >
                    {action.text}
                </Link>
            )}
        </Container>
    );
};

export default HBNote;

// -------------------------- Styles -------------------------- //

const Container = styled.div`
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 8px;
    border-radius: ${vars.radius.xs};
    background-color: ${vars.colors.surface.accent};
    max-width: 300px;
    padding: 16px;
    position: relative;

    i:first-child {
        display: flex;
    }
`;

const Close = styled(Close16)`
    position: absolute;
    display: flex;
    top: 8px;
    right: 8px;
`;

const Text = styled(HBText).attrs({ type: 'body14' })`
    grid-column: 2 / 4;
`;

const Description = styled(Text)`
    white-space: pre-line;
`;

const Title = styled(Text)`
    + ${Description} {
        grid-column: 1 / 4;
    }
`;

const Link = styled(HBLink)`
    grid-column: 3;
    margin: 0 -6px -6px 0;
`;
